import { inject, observer } from "mobx-react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";

import ExternalUserManagementStore from "../../../modules/Admin/ExternalUserManagement/store";
import {
  LanguageTypes,
  SSOExternalUserGridListDto,
} from "../../../modules/Admin/ExternalUserManagement/types";
import { MessageResultState } from "../../../types/general-types";
import { REDESIGN_NS } from "../../i18n";
import { Modal, ModalFooter } from "../../ui/Modal";

const EMPTY: SSOExternalUserGridListDto = {
  id: 0,
  nameSurname: "",
  email: "",
  username: "",
  password: "",
  phoneNumber: "",
  sendSmsVerification: false,
  sendEmailVerification: false,
  contentLanguage: LanguageTypes.Turkish,
};

const MIN_NAME = 3;
const MIN_USERNAME = 5;
const MIN_PASSWORD = 5;
const MIN_PHONE = 10;
const MAX_PHONE = 18;

interface Props {
  ExternalUserManagementStore?: ExternalUserManagementStore;
  isOpen: boolean;
  onClose: () => void;
  selected?: SSOExternalUserGridListDto;
  onSaved: () => void;
}

/** Alanlar ve doğrulama kuralları mevcut formla birebir aynıdır. */
const ExternalUserFormModal: FC<Props> = inject("ExternalUserManagementStore")(
  observer(
    ({
      ExternalUserManagementStore: store,
      isOpen,
      onClose,
      selected,
      onSaved,
    }: Props) => {
      const { t } = useTranslation(REDESIGN_NS);
      const { register, handleSubmit, errors } = useForm();

      const [data, setData] = useState<SSOExternalUserGridListDto>(
        selected || EMPTY
      );
      const [isBusy, setIsBusy] = useState(false);

      useEffect(() => {
        setData(selected || EMPTY);
      }, [selected]);

      const set = (patch: Partial<SSOExternalUserGridListDto>) =>
        setData((current) => ({ ...current, ...patch }));

      const submit = handleSubmit(async () => {
        if (!store) return;
        if (!data.sendSmsVerification && !data.sendEmailVerification) {
          toast.warning(t("Please select at least one verification method"));
          return;
        }

        const payload = {
          nameSurname: data.nameSurname.trim(),
          email: data.email.trim(),
          username: data.username.trim(),
          password: data.password.trim(),
          phoneNumber: data.phoneNumber.trim(),
          sendSmsVerification: data.sendSmsVerification,
          sendEmailVerification: data.sendEmailVerification,
          contentLanguage: data.contentLanguage,
        };

        setIsBusy(true);
        try {
          const response = data.id
            ? await store.update({ ...payload, id: data.id })
            : await store.create(payload);
          if (response.state === MessageResultState.SUCCESS) onSaved();
        } finally {
          setIsBusy(false);
        }
      });

      const required = t("Please fill out this field.").toString();
      const minLength = (value: number) => ({
        value,
        message: t("The given value must be at least {{value}} characters.", {
          value,
        }),
      });
      const maxLength = (value: number) => ({
        value,
        message: t("The given value must be at most {{value}} characters.", {
          value,
        }),
      });

      const invalid = (name: string) => (errors[name] ? " is-invalid" : "");
      const error = (name: string) =>
        errors[name] ? (
          <div className="sso-rd__error">{errors[name].message}</div>
        ) : null;

      return (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          width={840}
          title={data.id ? t("Update External User") : t("Add External User")}
          footer={
            <ModalFooter
              onClose={onClose}
              onSubmit={submit}
              submitLabel={t("Save")}
              isBusy={isBusy}
            />
          }
        >
          <div className="sso-rd__form-grid">
            <div className="sso-rd__field">
              <label className="sso-rd__label" htmlFor="sso-rd-ext-name">
                {t("Name Surname")}
                <span className="sso-rd__req">*</span>
              </label>
              <input
                id="sso-rd-ext-name"
                name="nameSurname"
                type="text"
                placeholder={t("Name Surname")}
                className={"sso-rd__input" + invalid("nameSurname")}
                value={data.nameSurname}
                ref={register({ required, minLength: minLength(MIN_NAME) })}
                onChange={(event) => set({ nameSurname: event.target.value })}
              />
              {error("nameSurname")}
            </div>

            <div className="sso-rd__field">
              <label className="sso-rd__label" htmlFor="sso-rd-ext-email">
                {t("Email")}
                <span className="sso-rd__req">*</span>
              </label>
              <input
                id="sso-rd-ext-email"
                name="email"
                type="text"
                placeholder={t("example@company.com")}
                className={"sso-rd__input" + invalid("email")}
                value={data.email}
                ref={register({
                  required,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("Invalid Email Address"),
                  },
                })}
                onChange={(event) => set({ email: event.target.value })}
              />
              {error("email")}
            </div>

            <div className="sso-rd__field">
              <label className="sso-rd__label" htmlFor="sso-rd-ext-username">
                {t("Username")}
                <span className="sso-rd__req">*</span>
              </label>
              <input
                id="sso-rd-ext-username"
                name="username"
                type="text"
                placeholder={t("username.example")}
                /* Burada başka bir kullanıcı oluşturuluyor; tarayıcı yöneticinin
                   kendi bilgilerini doldurmasın. */
                autoComplete="off"
                className={"sso-rd__input" + invalid("username")}
                value={data.username}
                ref={register({ required, minLength: minLength(MIN_USERNAME) })}
                onChange={(event) => set({ username: event.target.value })}
              />
              {error("username")}
            </div>

            <div className="sso-rd__field">
              <label className="sso-rd__label" htmlFor="sso-rd-ext-password">
                {t("Password")}
                <span className="sso-rd__req">*</span>
              </label>
              <input
                id="sso-rd-ext-password"
                name="password"
                type="password"
                placeholder={t("Password")}
                autoComplete="new-password"
                className={"sso-rd__input" + invalid("password")}
                value={data.password}
                ref={register({ required, minLength: minLength(MIN_PASSWORD) })}
                onChange={(event) => set({ password: event.target.value })}
              />
              {error("password")}
            </div>

            <div className="sso-rd__field">
              <label className="sso-rd__label" htmlFor="sso-rd-ext-phone">
                {t("Phone Number")}
                <span className="sso-rd__req">*</span>
              </label>
              <NumberFormat
                id="sso-rd-ext-phone"
                name="phoneNumber"
                format="(###) ###-####"
                mask="_"
                placeholder={t("(5xx) xxx-xxxx")}
                className={"sso-rd__input" + invalid("phoneNumber")}
                value={data.phoneNumber}
                getInputRef={register({
                  required,
                  minLength: minLength(MIN_PHONE),
                  maxLength: maxLength(MAX_PHONE),
                })}
                onValueChange={(value) => set({ phoneNumber: value.value })}
              />
              {error("phoneNumber")}
            </div>

            <div className="sso-rd__field-pair">
            <div className="sso-rd__field">
              <span className="sso-rd__label">
                {t("Verification Method")}
                <span className="sso-rd__req">*</span>
              </span>
              {/* Çoklu seçim; en az biri zorunlu (kaydetmede kontrol edilir). */}
              <div className="sso-rd__check-group">
                <label className="sso-rd__check">
                  <input
                    type="checkbox"
                    checked={data.sendSmsVerification}
                    onChange={(event) =>
                      set({ sendSmsVerification: event.target.checked })
                    }
                  />
                  {t("SMS")}
                </label>
                <label className="sso-rd__check">
                  <input
                    type="checkbox"
                    checked={data.sendEmailVerification}
                    onChange={(event) =>
                      set({ sendEmailVerification: event.target.checked })
                    }
                  />
                  {t("Email")}
                </label>
              </div>
            </div>

            <div className="sso-rd__field">
              <span className="sso-rd__label">
                {t("Content Language")}
                <span className="sso-rd__req">*</span>
              </span>
              {/* Tekli seçim. */}
              <div className="sso-rd__check-group">
                <label className="sso-rd__check">
                  <input
                    type="radio"
                    name="contentLanguage"
                    checked={data.contentLanguage === LanguageTypes.Turkish}
                    onChange={() => set({ contentLanguage: LanguageTypes.Turkish })}
                  />
                  {t("Turkish")}
                </label>
                <label className="sso-rd__check">
                  <input
                    type="radio"
                    name="contentLanguage"
                    checked={data.contentLanguage === LanguageTypes.English}
                    onChange={() => set({ contentLanguage: LanguageTypes.English })}
                  />
                  {t("English")}
                </label>
              </div>
            </div>
            </div>
          </div>
        </Modal>
      );
    }
  )
) as FC<Props>;

export default ExternalUserFormModal;
